import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import AppLayout from "@/components/AppLayout";
import api from "@/lib/api";
import { ArrowLeft, CheckCircle2, ArrowRight, Play } from "lucide-react";
import TTSControls, { stripMarkdownForSpeech } from "@/components/TTSControls";

// Minimal, safe markdown → HTML renderer for the readings we ship.
function renderMarkdown(md) {
  if (!md) return "";
  let html = md;
  // Escape
  html = html.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  // Headings
  html = html.replace(/^### (.*)$/gm, "<h3>$1</h3>");
  html = html.replace(/^## (.*)$/gm, "<h2>$1</h2>");
  html = html.replace(/^# (.*)$/gm, "<h1>$1</h1>");
  // Bold
  html = html.replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>");
  // Inline code
  html = html.replace(/`([^`]+)`/g, "<code>$1</code>");
  // Tables (simple GFM)
  html = html.replace(/((?:^\|.*\|\s*\n)+)/gm, (block) => {
    const rows = block.trim().split("\n").map((r) => r.trim());
    if (rows.length < 2) return block;
    const header = rows[0].slice(1, -1).split("|").map((c) => c.trim());
    const body = rows.slice(2).map((r) => r.slice(1, -1).split("|").map((c) => c.trim()));
    const th = header.map((c) => `<th>${c}</th>`).join("");
    const bd = body.map((row) => `<tr>${row.map((c) => `<td>${c}</td>`).join("")}</tr>`).join("");
    return `<table><thead><tr>${th}</tr></thead><tbody>${bd}</tbody></table>`;
  });
  // Lists
  html = html.replace(/^(?:- |\* )(.*)$/gm, "<li>$1</li>");
  html = html.replace(/(<li>[\s\S]*?<\/li>)(?=(?:\n(?!<li>))|$)/g, (m) => `<ul>${m}</ul>`);
  // Paragraphs
  html = html.split(/\n{2,}/).map((chunk) => {
    if (/^<(h\d|ul|ol|table|pre|blockquote)/.test(chunk.trim())) return chunk;
    return `<p>${chunk.replace(/\n/g, " ")}</p>`;
  }).join("\n");
  return html;
}

export default function LessonPage() {
  const { moduleId, lessonId } = useParams();
  const nav = useNavigate();
  const [lesson, setLesson] = useState(null);
  const [module, setModule] = useState(null);
  const [completed, setCompleted] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      const [l, m, p] = await Promise.all([
        api.get(`/modules/${moduleId}/lessons/${lessonId}`),
        api.get(`/modules/${moduleId}`),
        api.get("/progress"),
      ]);
      setLesson(l.data);
      setModule(m.data);
      const done = (p.data?.items || []).some((i) => i.module_id === parseInt(moduleId) && i.lesson_id === lessonId);
      setCompleted(done);
    })();
  }, [moduleId, lessonId]);

  async function markComplete() {
    setBusy(true);
    try {
      await api.post("/progress/complete", { module_id: parseInt(moduleId), lesson_id: lessonId });
      setCompleted(true);
    } finally {
      setBusy(false);
    }
  }

  const nextLesson = React.useMemo(() => {
    if (!module || !lesson) return null;
    const idx = module.lessons.findIndex((l) => l.id === lesson.id);
    return module.lessons[idx + 1] || null;
  }, [module, lesson]);

  if (!lesson || !module) return <AppLayout><div className="p-12 text-neutral-500">Loading lesson…</div></AppLayout>;

  return (
    <AppLayout>
      <div className="px-6 md:px-10 py-10 max-w-4xl mx-auto" data-testid="lesson-page">
        <Link to={`/modules/${moduleId}`} className="text-xs text-neutral-500 hover:text-[var(--dw-navy)] inline-flex items-center gap-1 mb-6" data-testid="back-to-module">
          <ArrowLeft className="w-3.5 h-3.5" strokeWidth={1.5} /> Back to Module {module.id}
        </Link>

        <div className="mb-2 overline">
          Module {module.id} · {lesson.type === "reading" ? "Reading" : lesson.type === "study-doc" ? "Study Document" : "Video Lecture"}
        </div>
        <h1 className="font-serif text-4xl md:text-5xl text-[var(--dw-navy)] leading-tight tracking-tight mb-8">
          {lesson.title}
        </h1>

        {(lesson.type === "reading" || lesson.type === "study-doc") && (
          <div className="mb-6" data-testid="lesson-tts">
            <TTSControls getText={() => stripMarkdownForSpeech(lesson.content)} label="Listen to this reading" />
          </div>
        )}

        {lesson.type === "video" && lesson.video_id && (
          <div className="mb-8 aspect-video border border-[var(--dw-navy)]/10 rounded-sm overflow-hidden bg-black" data-testid="video-player">
            <iframe
              className="w-full h-full"
              src={`https://www.youtube.com/embed/${lesson.video_id}`}
              title={lesson.title}
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </div>
        )}

        <article className="dw-prose" data-testid="lesson-content" dangerouslySetInnerHTML={{ __html: renderMarkdown(lesson.content) }} />

        {/* Actions */}
        <div className="mt-12 pt-8 border-t border-[var(--dw-navy)]/10 flex flex-wrap items-center justify-between gap-4">
          {completed ? (
            <div className="flex items-center gap-2 text-emerald-700 text-sm" data-testid="lesson-completed-badge">
              <CheckCircle2 className="w-5 h-5" strokeWidth={1.5} /> Lesson complete
            </div>
          ) : (
            <button
              onClick={markComplete}
              disabled={busy}
              data-testid="mark-complete-btn"
              className="dw-btn-primary rounded-sm px-5 py-2.5 text-sm inline-flex items-center gap-2 disabled:opacity-50"
            >
              {busy ? "Saving…" : "Mark as complete"}
              <CheckCircle2 className="w-4 h-4" strokeWidth={1.5} />
            </button>
          )}
          {nextLesson && (
            <Link
              to={nextLesson.type === "quiz" ? `/modules/${moduleId}/quiz` : `/modules/${moduleId}/lesson/${nextLesson.id}`}
              data-testid="next-lesson-btn"
              className="dw-btn-outline rounded-sm px-5 py-2.5 text-sm inline-flex items-center gap-2 hover:border-[var(--dw-navy)]"
            >
              {nextLesson.type === "quiz" ? "Take the assessment" : "Next lesson"}
              <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
            </Link>
          )}
        </div>
      </div>
    </AppLayout>
  );
}
